/**
 * `pending-saved-view-schedule` v0 — wire contracts.
 *
 * One row = one recurring schedule of "fan out from a saved view".
 * The saved view's DSL is resolved to a flow set at *trigger* time,
 * so adding/removing flows that match the DSL automatically reshapes
 * the next tick's run set.
 */
import { z } from 'zod';

export const SavedViewScheduleSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  userId: z.string().uuid(),
  savedViewId: z.string().uuid(),
  cron: z.string().min(1).max(200),
  environment: z.string().nullable(),
  enabled: z.boolean(),
  nextRunAt: z.string(),
  lastRunAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SavedViewSchedule = z.infer<typeof SavedViewScheduleSchema>;

export const CreateSavedViewScheduleRequestSchema = z.object({
  savedViewId: z.string().uuid(),
  cron: z.string().min(1).max(200),
  environment: z.string().min(1).max(64).optional(),
  enabled: z.boolean().default(true),
});
export type CreateSavedViewScheduleRequest = z.infer<typeof CreateSavedViewScheduleRequestSchema>;

export const UpdateSavedViewScheduleRequestSchema = z
  .object({
    cron: z.string().min(1).max(200).optional(),
    environment: z.string().min(1).max(64).nullable().optional(),
    enabled: z.boolean().optional(),
  })
  .refine((p) => Object.keys(p).length > 0, { message: 'At least one field must be provided.' });
export type UpdateSavedViewScheduleRequest = z.infer<typeof UpdateSavedViewScheduleRequestSchema>;

export const ListSavedViewSchedulesResponseSchema = z.object({
  count: z.number().int().nonnegative(),
  schedules: z.array(SavedViewScheduleSchema),
});
export type ListSavedViewSchedulesResponse = z.infer<typeof ListSavedViewSchedulesResponseSchema>;
